import React, { useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CreditCard,
  ShieldCheck,
  Wallet,
  Trash2,
  CheckCircle2,
} from "lucide-react";

import Iyzico3DSModal from "@/components/Iyzico3DSModal";
import { threedsInitialize } from "@/lib/subscriptionPayment";
import { connectPayHub } from "@/lib/payHub";

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const getPlanValue = (plan, key) => {
  if (!plan) return null;

  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  const lowerKey = key.toLowerCase();

  return (
    plan[key] ??
    plan[pascalKey] ??
    plan[snakeKey] ??
    plan[lowerKey] ??
    null
  );
};

const formatMoney = (value, currency = "₺") => {
  const number = Number(value || 0);

  return `${number.toLocaleString("tr-TR", {
    minimumFractionDigits: number % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
};

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const formatShortDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getSubscriptionStatusLabel = (status) => {
  const numericStatus = Number(status);

  if (numericStatus === 1) return "Aktif";
  if (numericStatus === 0) return "Pasif";
  if (numericStatus === 2) return "İptal Edildi";
  if (numericStatus === 3) return "Süresi Doldu";

  return "Bilinmiyor";
};

const formatPlanLimit = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString("tr-TR")
    : "-";

function PlanLimits({plan}) {
  return (
    <div className="mt-3 space-y-1 text-xs leading-5 text-zinc-600">
      {[
        ["maxPromotions", "Kampanya limiti"],
        ["maxCategories", "Kategori limiti"],
        ["maxProducts", "Ürün limiti"],
        ["maxUsers", "Kullanıcı limiti"],
      ].map(([key, label]) => (
        <div key={key}>{label}: {formatPlanLimit(getPlanValue(plan, key))}</div>
      ))}
    </div>
  );
}

export default function SubscriptionPage() {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [allSubscriptions, setAllSubscriptions] = useState([]);

  const [paymentMode, setPaymentMode] = useState("new");

  const [registeredCards, setRegisteredCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [selectedSavedCardId, setSelectedSavedCardId] = useState(null);

  const [cardHolderName, setCardHolderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expireMonth, setExpireMonth] = useState("");
  const [expireYear, setExpireYear] = useState("");
  const [cvc, setCvc] = useState("");
  const [identityNumber, setIdentityNumber] = useState("");
  const [autoRenew, setAutoRenew] = useState(true);
  const [saveCard, setSaveCard] = useState(false);
  const [mobileStep, setMobileStep] = useState(1);

  const [address, setAddress] = useState({
    type: "TESLIMAT",
    title: "",
    contactName: "",
    addressLine: "",
    city: "",
    country: "Türkiye",
    zipCode: "",
  });

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payHtmlContent, setPayHtmlContent] = useState("");

  const hubRef = useRef(null);
  const providerRef = useRef(null);

  useEffect(() => {
    loadCurrentSubscription();
    loadPlans();
    loadRegisteredCards();

    return () => {
      try {
        hubRef.current?.stop?.();
      } catch {}
    };
  }, []);

  const normalizeExpireYear = (value) => {
    const digits = onlyDigits(value);
    if (digits.length === 4) return digits.slice(-2);
    return digits;
  };

  const formatCardNumber = (value) => {
    const digits = onlyDigits(value).slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  };

  const maskCardNumber = (value) => {
    const digits = onlyDigits(value);
    if (digits.length >= 4) return `•••• •••• •••• ${digits.slice(-4)}`;
    return "•••• •••• •••• ••••";
  };

  const getCardBrand = (value) => {
    const digits = onlyDigits(value);
    if (digits.startsWith("4")) return "Visa";
    if (/^5[1-5]/.test(digits)) return "Mastercard";
    if (/^62/.test(digits)) return "Troy";
    return "Kart";
  };

  const normalizeRegisteredCard = (card) => ({
    id: card?.id ?? card?.Id ?? null,
    provider: card?.provider ?? card?.Provider ?? "",
    alias: card?.alias ?? card?.Alias ?? "",
    cardHolderName: card?.cardHolderName ?? card?.CardHolderName ?? "",
    cardNumber: card?.cardNumber ?? card?.CardNumber ?? "",
    expireMonth: card?.expireMonth ?? card?.ExpireMonth ?? "",
    expireYear: card?.expireYear ?? card?.ExpireYear ?? "",
    cardUserKey: card?.cardUserKey ?? card?.CardUserKey ?? "",
    cardToken: card?.cardToken ?? card?.CardToken ?? "",
  });

  const loadCurrentSubscription = async () => {
    try {
      setSubscriptionLoading(true);

      const data = await api.getCurrentSubscription();

      setCurrentSubscription(data?.current || null);
      setAllSubscriptions(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error("Mevcut abonelik bilgisi yüklenemedi:", error);
      setCurrentSubscription(null);
      setAllSubscriptions([]);
      toast.error("Abonelik durumu yüklenirken hata oluştu.");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const data = await api.getSubscriptionPlans();
      setPlans(data?.items || data || []);
    } catch (err) {
      console.error(err);
      toast.error("Abonelik planları yüklenirken hata oluştu.");
    }
  };

  const loadRegisteredCards = async () => {
    try {
      setLoadingCards(true);

      const res = await api.getBusinessRegisteredCards({
        Page: 0,
        PageSize: 50,
      });

      const raw =
        res?.data?.items ||
        res?.data?.data?.items ||
        res?.data?.data ||
        res?.data ||
        [];

      const list = Array.isArray(raw) ? raw.map(normalizeRegisteredCard) : [];

      setRegisteredCards(list);

      if (list.length > 0) {
        setSelectedSavedCardId(list[0].id);
      } else {
        setSelectedSavedCardId(null);
        setPaymentMode("new");
      }
    } catch (err) {
      console.error("card list error", err);
      setRegisteredCards([]);
      setSelectedSavedCardId(null);
      setPaymentMode("new");
    } finally {
      setLoadingCards(false);
    }
  };

  const handleDeleteSavedCard = async (id) => {
    try {
      await api.deleteBusinessRegisteredCard(id);
      toast.success("Kayıtlı kart silindi.");

      const nextCards = registeredCards.filter((card) => card.id !== id);
      setRegisteredCards(nextCards);

      if (selectedSavedCardId === id) {
        setSelectedSavedCardId(nextCards[0]?.id ?? null);
      }

      if (nextCards.length === 0) {
        setPaymentMode("new");
      }
    } catch (err) {
      console.error(err);
      toast.error("Kart silinemedi.");
    }
  };

  const getToken = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      return (
        user?.token ||
        user?.accessToken ||
        localStorage.getItem("token") ||
        null
      );
    } catch {
      return localStorage.getItem("token");
    }
  };

  const closePayModal = async () => {
    setPayModalOpen(false);
    setPayHtmlContent("");
    providerRef.current = null;

    try {
      if (hubRef.current) {
        await hubRef.current.stop();
        hubRef.current = null;
      }
    } catch {}
  };

  const validateAddress = () => {
    if (
      !address.contactName ||
      !address.addressLine ||
      !address.city ||
      !address.zipCode
    ) {
      toast.error("Lütfen teslimat adresi bilgilerini doldurun.");
      return false;
    }

    return true;
  };

  const validateNewCardForm = () => {
    if (!cardHolderName.trim()) {
      toast.error("Lütfen kart üzerindeki ismi girin.");
      return false;
    }

    if (onlyDigits(cardNumber).length < 16) {
      toast.error("Lütfen geçerli bir kart numarası girin.");
      return false;
    }

    const month = onlyDigits(expireMonth);
    if (month.length !== 2 || Number(month) < 1 || Number(month) > 12) {
      toast.error("Lütfen geçerli bir son kullanma ayı girin.");
      return false;
    }

    const year = normalizeExpireYear(expireYear);
    if (year.length !== 2) {
      toast.error("Lütfen geçerli bir son kullanma yılı girin.");
      return false;
    }

    if (onlyDigits(cvc).length < 3) {
      toast.error("Lütfen geçerli bir CVC girin.");
      return false;
    }

    return true;
  };

  const validateForm = () => {
    if (!selectedPlanId) {
      toast.error("Lütfen bir plan seçin.");
      return false;
    }

    if (!identityNumber || onlyDigits(identityNumber).length !== 11) {
      toast.error("Lütfen geçerli bir TC Kimlik numarası girin.");
      return false;
    }

    if (!validateAddress()) return false;

    if (paymentMode === "saved") {
      const selected = registeredCards.find(
        (card) => card.id === selectedSavedCardId
      );

      if (!selected) {
        toast.error("Lütfen kayıtlı bir kart seçin.");
        return false;
      }

      return true;
    }

    return validateNewCardForm();
  };

  const handlePay = async () => {
    if (!validateForm()) return;

    const token = getToken();

    if (!token) {
      toast.error("Token bulunamadı. Lütfen yeniden giriş yapın.");
      return;
    }

    const selectedSavedCard =
      registeredCards.find((card) => card.id === selectedSavedCardId) || null;

    setLoading(true);

    try {
      const addressPayload = {
        type: "TESLIMAT",
        title: address.title,
        contactName: address.contactName,
        addressLine: address.addressLine,
        city: address.city,
        country: address.country || "Türkiye",
        zipCode: address.zipCode,
      };

      const addressRes = await api.addAddress(addressPayload);

      const addressId =
        addressRes?.data?.id ||
        addressRes?.data?.data?.id ||
        addressRes?.id ||
        addressRes?.result?.id;

      if (!addressId) {
        toast.error("Adres kaydedilemedi. Lütfen tekrar deneyin.");
        return;
      }

      const payload =
        paymentMode === "saved" && selectedSavedCard
          ? {
              subscriptionPlanId: selectedPlanId,
              identityNumber: onlyDigits(identityNumber),
              shippingAddressId: addressId,
              billingAddressId: null,
              useShippingAsBilling: true,
              autoRenew,
              registeredPaymentCard: {
                cardUserKey: selectedSavedCard.cardUserKey || "",
                cardToken: selectedSavedCard.cardToken || "",
              },
              paymentCard: null,
            }
          : {
              subscriptionPlanId: selectedPlanId,
              identityNumber: onlyDigits(identityNumber),
              shippingAddressId: addressId,
              billingAddressId: null,
              useShippingAsBilling: true,
              autoRenew,
              registeredPaymentCard: null,
              paymentCard: {
                cardHolderName: cardHolderName.trim(),
                cardNumber: onlyDigits(cardNumber),
                expireMonth: onlyDigits(expireMonth),
                expireYear: normalizeExpireYear(expireYear),
                cvc: onlyDigits(cvc),
                registerCard: saveCard ? 1 : 0,
              },
            };

      const data = await threedsInitialize({ token, payload });

      if (!data?.success || !data?.htmlContent || !data?.providerReference) {
        toast.error(data?.message || "3D Secure başlatılamadı.");
        return;
      }

      providerRef.current = data.providerReference;
      setPayHtmlContent(data.htmlContent);
      setPayModalOpen(true);

      hubRef.current = await connectPayHub({
        providerReference: data.providerReference,
        onMessage: (msg) => {
          if (msg?.providerReference !== providerRef.current) return;

          const ok = msg?.status === "success" && msg?.mdStatus === "1";

          if (ok) {
            toast.success(
              paymentMode === "saved"
                ? "Kayıtlı kart ile ödeme başarılı!"
                : saveCard
                ? "Ödeme başarılı, kart kaydetme talebi gönderildi."
                : "Ödeme başarılı!"
            );

            setTimeout(async () => {
              await loadRegisteredCards();
              await loadCurrentSubscription();
            }, 800);
          } else {
            toast.error("Ödeme başarısız!");
          }

          setTimeout(() => closePayModal(), 1500);
        },
      });
    } catch (err) {
      console.error(err);

      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message;

      toast.error(detail || "Ödeme sırasında bir hata oluştu.");
      await closePayModal();
    } finally {
      setLoading(false);
    }
  };

  const currentPlan =
    plans.find(
      (plan) =>
        String(getPlanValue(plan, "id")) ===
        String(currentSubscription?.planId)
    ) || null;

  const currentPlanName =
    getPlanValue(currentPlan, "name") ||
    getPlanValue(currentPlan, "title") ||
    (currentSubscription?.planId
      ? `Plan #${currentSubscription.planId}`
      : "Aktif Plan");

  const currentPlanDescription =
    getPlanValue(currentPlan, "description") ||
    "Aktif aboneliğiniz Web Menü hizmetiniz için kullanılmaktadır.";

  const currentPlanCurrency = getPlanValue(currentPlan, "currency") || "₺";
  const currentPlanPrice = Number(getPlanValue(currentPlan, "price") || 0);

  const remainingDays = currentSubscription?.endDate
    ? Math.max(
        0,
        Math.ceil(
          (new Date(currentSubscription.endDate) - new Date()) /
            (1000 * 60 * 60 * 24)
        )
      )
    : null;

  const sortedSubscriptions = [...allSubscriptions].sort(
    (a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0)
  );

  const selectedPlan =
    plans.find(
      (plan) => String(getPlanValue(plan, "id")) === String(selectedPlanId)
    ) || null;

  const selectedPlanName =
    getPlanValue(selectedPlan, "name") ||
    getPlanValue(selectedPlan, "title") ||
    "Seçilmedi";

  const selectedPlanDescription =
    getPlanValue(selectedPlan, "description") || "Abonelik planınız seçildi.";

  const selectedCurrency = getPlanValue(selectedPlan, "currency") || "₺";

  const subtotal = Number(getPlanValue(selectedPlan, "price") || 0);

  const vatPercent = Number(getPlanValue(selectedPlan, "vatPercent") || 20);

  const vatPriceFromApi = getPlanValue(selectedPlan, "vatPrice");

  const vatPrice =
    vatPriceFromApi !== null && vatPriceFromApi !== undefined
      ? Number(vatPriceFromApi)
      : subtotal * (vatPercent / 100);

  const vatIncludedPriceFromApi = getPlanValue(
    selectedPlan,
    "vatIncludedPrice"
  );

  const vatIncludedPrice =
    vatIncludedPriceFromApi !== null && vatIncludedPriceFromApi !== undefined
      ? Number(vatIncludedPriceFromApi)
      : subtotal + vatPrice;

  const previewBrand = getCardBrand(cardNumber);
  const previewNumber = maskCardNumber(cardNumber);
  const previewName = cardHolderName || "AD SOYAD";
  const previewMonth = expireMonth || "MM";
  const previewYear = normalizeExpireYear(expireYear) || "YY";

  const handleMobileContinue = () => {
    if (mobileStep === 1) {
      if (!selectedPlanId) {
        toast.error("Lütfen bir plan seçin.");
        return;
      }

      setMobileStep(2);
      return;
    }

    if (mobileStep === 2) {
      if (onlyDigits(identityNumber).length !== 11) {
        toast.error("Lütfen geçerli bir TC Kimlik numarası girin.");
        return;
      }

      if (!validateAddress()) return;

      setMobileStep(3);
      return;
    }

    if (mobileStep === 3) {
      if (paymentMode === "saved") {
        const selected = registeredCards.find(
          (card) => card.id === selectedSavedCardId
        );

        if (!selected) {
          toast.error("Lütfen kayıtlı bir kart seçin.");
          return;
        }
      } else if (!validateNewCardForm()) {
        return;
      }

      setMobileStep(4);
      return;
    }

    handlePay();
  };

  const handleMobileBack = () => {
    setMobileStep((step) => Math.max(1, step - 1));
  };

  if (subscriptionLoading) {
    return (
      <AdminLayout>
        <div className="min-h-full bg-[#F8F8F8] px-5 pb-10 pt-4 md:hidden">
          <div className="rounded-[22px] border border-[#E4E4E7] bg-white p-5">
            <div className="h-4 w-24 animate-pulse rounded-full bg-[#E4E4E7]" />
            <div className="mt-4 h-8 w-48 animate-pulse rounded-full bg-[#F4F4F5]" />
            <div className="mt-3 h-4 w-full animate-pulse rounded-full bg-[#F4F4F5]" />
            <div className="mt-8 h-32 animate-pulse rounded-[18px] bg-[#F4F4F5]" />
          </div>
        </div>

        <div className="hidden w-full justify-center md:flex">
          <div className="w-full max-w-[1180px]">
            <div className="rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_22px_65px_rgba(24,24,27,0.05)]">
              <div className="h-7 w-56 animate-pulse rounded-full bg-zinc-100" />
              <div className="mt-4 h-4 w-80 max-w-full animate-pulse rounded-full bg-zinc-100" />

              <div className="mt-8 grid gap-4 md:grid-cols-3">
                <div className="h-28 animate-pulse rounded-3xl bg-zinc-100" />
                <div className="h-28 animate-pulse rounded-3xl bg-zinc-100" />
                <div className="h-28 animate-pulse rounded-3xl bg-zinc-100" />
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (currentSubscription) {
    return (
      <AdminLayout>
        <div className="min-h-full bg-[#F8F8F8] px-5 pb-10 pt-4 md:hidden">
          <section className="rounded-[22px] border border-[#E4E4E7] bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <div
                className={`inline-flex min-h-8 items-center gap-1.5 rounded-full px-2.5 text-[10px] font-extrabold ${
                  Number(currentSubscription.status) === 1
                    ? "bg-green-100 text-green-700"
                    : "bg-[#F4F4F5] text-[#71717A]"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    Number(currentSubscription.status) === 1
                      ? "bg-green-600"
                      : "bg-[#A1A1AA]"
                  }`}
                />
                {getSubscriptionStatusLabel(currentSubscription.status)}
              </div>

              <ShieldCheck className="h-5 w-5 text-[#EA580C]" />
            </div>

            <h1 className="mt-4 text-[26px] font-extrabold leading-8 text-[#18181B]">
              {currentPlanName}
            </h1>

            <div className="mt-5 flex items-end gap-2">
              <span className="text-[44px] font-extrabold leading-none tabular-nums text-[#EA580C]">
                {remainingDays !== null ? remainingDays : "-"}
              </span>
              <span className="pb-1 text-[15px] font-bold text-[#EA580C]">
                gün kaldı
              </span>
            </div>

            <p className="mt-1 text-[13px] text-[#71717A]">
              {formatShortDate(currentSubscription.endDate)} tarihine kadar
            </p>

            <div className="mt-5 flex items-center justify-between gap-4 border-t border-[#E4E4E7] pt-4">
              <div>
                <p className="text-[13px] font-bold text-[#18181B]">
                  Otomatik yenileme
                </p>
                <p className="mt-0.5 text-[10px] leading-4 text-[#A1A1AA]">
                  Dönem sonunda planın yenilenme durumu
                </p>
              </div>

              <span
                className={`shrink-0 rounded-full px-2.5 py-1.5 text-[10px] font-extrabold ${
                  currentSubscription.autoRenew
                    ? "bg-green-100 text-green-700"
                    : "bg-[#F4F4F5] text-[#71717A]"
                }`}
              >
                {currentSubscription.autoRenew ? "Açık" : "Kapalı"}
              </span>
            </div>
          </section>

          {currentPlanPrice > 0 ? (
            <div className="mt-3 rounded-[17px] bg-[#FFF7ED] px-4 py-3.5">
              <p className="text-[10px] font-extrabold tracking-[0.06em] text-[#EA580C]">
                PAKET FİYATI
              </p>
              <p className="mt-1 text-xl font-extrabold text-[#18181B]">
                {formatMoney(currentPlanPrice, currentPlanCurrency)}
              </p>
            </div>
          ) : null}

          <p className="mb-2 ml-1 mt-6 text-[10px] font-extrabold tracking-[0.07em] text-[#A1A1AA]">
            YÖNETİM
          </p>

          <div className="overflow-hidden rounded-[18px] border border-[#E4E4E7] bg-white">
            <details>
              <summary className="flex min-h-[72px] cursor-pointer list-none items-center gap-3 px-4 py-3 active:bg-[#FAFAFA]">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFF7ED] text-[#EA580C]">
                  <CreditCard className="h-[18px] w-[18px]" />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-bold text-[#18181B]">
                    Kayıtlı Kartlar
                  </p>
                  <p className="mt-0.5 text-[11px] text-[#71717A]">
                    {registeredCards.length
                      ? `${registeredCards.length} kayıtlı kart`
                      : "Henüz kayıtlı kart yok"}
                  </p>
                </div>

                <span className="rounded-full bg-[#FFF7ED] px-2 py-1 text-[10px] font-extrabold text-[#EA580C]">
                  {registeredCards.length}
                </span>
              </summary>

              <div className="border-t border-[#E4E4E7] px-4 py-3">
                {loadingCards ? (
                  <p className="py-5 text-center text-sm text-[#71717A]">
                    Kayıtlı kartlar yükleniyor...
                  </p>
                ) : registeredCards.length ? (
                  <div className="space-y-2.5">
                    {registeredCards.map((card) => (
                      <div
                        key={card.id ?? card.cardToken}
                        className="rounded-[16px] border border-[#E4E4E7] bg-[#FAFAFA] p-4"
                      >
                        <p className="text-sm font-extrabold text-[#18181B]">
                          {card.alias || getCardBrand(card.cardNumber)}
                        </p>
                        <p className="mt-2 text-sm tracking-[0.16em] text-[#52525B]">
                          {maskCardNumber(card.cardNumber)}
                        </p>
                        <p className="mt-1.5 text-[11px] text-[#A1A1AA]">
                          {card.cardHolderName || "Kart sahibi bilgisi yok"}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-5 text-center text-sm text-[#71717A]">
                    Henüz kayıtlı kartınız bulunmuyor.
                  </p>
                )}
              </div>
            </details>

            <div className="ml-16 h-px bg-[#E4E4E7]" />

            <details>
              <summary className="flex min-h-[72px] cursor-pointer list-none items-center gap-3 px-4 py-3 active:bg-[#FAFAFA]">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F4F4F5] text-[#52525B]">
                  <Wallet className="h-[18px] w-[18px]" />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-bold text-[#18181B]">
                    Geçmiş Abonelikler
                  </p>
                  <p className="mt-0.5 text-[11px] text-[#71717A]">
                    {sortedSubscriptions.length
                      ? `${sortedSubscriptions.length} abonelik kaydı`
                      : "Geçmiş abonelik bulunmuyor"}
                  </p>
                </div>

                <span className="rounded-full bg-[#F4F4F5] px-2 py-1 text-[10px] font-extrabold text-[#71717A]">
                  {sortedSubscriptions.length}
                </span>
              </summary>

              <div className="border-t border-[#E4E4E7] px-4 py-3">
                {sortedSubscriptions.length ? (
                  <div className="space-y-2.5">
                    {sortedSubscriptions.map((subscription) => {
                      const plan =
                        plans.find(
                          (item) =>
                            String(getPlanValue(item, "id")) ===
                            String(subscription.planId)
                        ) || null;

                      const planName =
                        getPlanValue(plan, "name") ||
                        getPlanValue(plan, "title") ||
                        `Plan #${subscription.planId}`;

                      return (
                        <div
                          key={subscription.id ?? subscription.createdDate}
                          className="rounded-[16px] border border-[#E4E4E7] bg-[#FAFAFA] p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-extrabold text-[#18181B]">
                                {planName}
                              </p>
                              <p className="mt-1 text-[10px] leading-4 text-[#71717A]">
                                {formatShortDate(subscription.startDate)} —{" "}
                                {formatShortDate(subscription.endDate)}
                              </p>
                            </div>

                            <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[10px] font-bold text-[#52525B]">
                              {getSubscriptionStatusLabel(subscription.status)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="py-5 text-center text-sm text-[#71717A]">
                    Geçmiş abonelik kaydı bulunamadı.
                  </p>
                )}
              </div>
            </details>
          </div>

          <div className="mt-4 rounded-[17px] bg-[#FFF7ED] p-4">
            <p className="text-xs font-extrabold text-[#EA580C]">
              Aboneliğiniz aktif
            </p>
            <p className="mt-1 text-[11px] leading-[17px] text-[#9A3412]">
              Web Menü vitrininiz abonelik süreniz boyunca müşterileriniz tarafından
              kullanılabilir.
            </p>
          </div>
        </div>

        <div className="hidden w-full justify-center md:flex">
          <div className="w-full max-w-[1180px] space-y-6">
            <div className="relative overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_22px_65px_rgba(24,24,27,0.05)] md:flex md:items-start md:justify-between md:gap-8">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.13em] text-orange-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                  Abonelik Durumu
                </div>
                <h1 className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[40px]">
                  Web Menü aboneliğiniz aktif.
                </h1>
                <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-zinc-500">
                  Paket sürenizi, yenileme durumunuzu, kayıtlı kartlarınızı ve
                  geçmiş aboneliklerinizi tek yerden takip edin.
                </p>
              </div>

              <div className="inline-flex shrink-0 items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-bold text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                Abonelik aktif
              </div>
            </div>

            <section className="overflow-hidden rounded-[30px] border border-orange-200 bg-gradient-to-br from-orange-50/80 via-white to-white p-7 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <div className="flex flex-col gap-7 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#EA580C] shadow-sm">
                    Aktif Paket
                  </div>

                  <h2 className="mt-4 text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 md:text-4xl">
                    {currentPlanName}
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-600">
                    {currentPlanDescription}
                  </p>

                  {currentPlanPrice > 0 && (
                    <div className="mt-5">
                      <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">
                        Paket Fiyatı
                      </div>
                      <div className="mt-1 text-3xl font-bold text-zinc-950">
                        {formatMoney(currentPlanPrice, currentPlanCurrency)}
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-[24px] border border-zinc-200/80 bg-white/90 p-5 shadow-[0_12px_30px_rgba(24,24,27,0.05)] backdrop-blur lg:min-w-[320px]">
                  <div className="text-sm text-zinc-500">Durum</div>
                  <div className="mt-1 text-xl font-semibold text-emerald-700">
                    {getSubscriptionStatusLabel(currentSubscription.status)}
                  </div>

                  <div className="mt-5 grid gap-4 text-sm">
                    <div>
                      <div className="text-zinc-500">Başlangıç Tarihi</div>
                      <div className="mt-1 font-medium text-zinc-950">
                        {formatDate(currentSubscription.startDate)}
                      </div>
                    </div>

                    <div>
                      <div className="text-zinc-500">Bitiş Tarihi</div>
                      <div className="mt-1 font-medium text-zinc-950">
                        {formatDate(currentSubscription.endDate)}
                      </div>
                    </div>

                    <div>
                      <div className="text-zinc-500">Otomatik Yenileme</div>
                      <div className="mt-1 font-medium text-zinc-950">
                        {currentSubscription.autoRenew ? "Açık" : "Kapalı"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-[24px] border border-zinc-200/80 bg-white p-5 shadow-[0_12px_35px_rgba(24,24,27,0.035)]">
                <div className="text-sm text-zinc-500">Kalan Süre</div>
                <div className="mt-2 text-2xl font-semibold text-zinc-950">
                  {remainingDays !== null ? `${remainingDays} gün` : "-"}
                </div>
                <p className="mt-2 text-xs text-zinc-500">
                  Aboneliğinizin bitiş tarihine kalan süre.
                </p>
              </div>

              <div className="rounded-[24px] border border-zinc-200/80 bg-white p-5 shadow-[0_12px_35px_rgba(24,24,27,0.035)]">
                <div className="text-sm text-zinc-500">Bitiş Tarihi</div>
                <div className="mt-2 text-lg font-semibold text-zinc-950">
                  {formatDate(currentSubscription.endDate)}
                </div>
                <p className="mt-2 text-xs text-zinc-500">
                  Bu tarihe kadar Web Menü vitrininiz aktif kalır.
                </p>
              </div>

              <div className="rounded-[24px] border border-zinc-200/80 bg-white p-5 shadow-[0_12px_35px_rgba(24,24,27,0.035)]">
                <div className="text-sm text-zinc-500">Otomatik Yenileme</div>
                <div className="mt-2 text-2xl font-semibold text-zinc-950">
                  {currentSubscription.autoRenew ? "Açık" : "Kapalı"}
                </div>
                <p className="mt-2 text-xs text-zinc-500">
                  {currentSubscription.autoRenew
                    ? "Aboneliğiniz dönem sonunda otomatik yenilenir."
                    : "Dönem sonunda yenileme işlemi manuel yapılmalıdır."}
                </p>
              </div>
            </div>

            <section className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                    Kayıtlı Kartlarım
                  </h2>
                  <p className="mt-1 text-sm text-zinc-500">
                    Abonelik ödemelerinde kullanmak üzere kaydedilen kartlarınız
                    burada güvenli şekilde listelenir.
                  </p>
                </div>

                <div className="inline-flex w-fit items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-600">
                  <CreditCard className="h-4 w-4" />
                  {registeredCards.length} kayıtlı kart
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {loadingCards ? (
                  <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-8 text-center text-sm text-zinc-500">
                    Kayıtlı kartlar yükleniyor...
                  </div>
                ) : registeredCards.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-8 text-center text-sm text-zinc-500">
                    Henüz kayıtlı kartınız bulunmuyor. Ödeme sırasında
                    “Kartımı kaydet” seçeneğini işaretlerseniz kartınız burada
                    görünebilir.
                  </div>
                ) : (
                  registeredCards.map((card) => {
                    const brand = getCardBrand(card.cardNumber);
                    const displayNumber = card.cardNumber
                      ? maskCardNumber(card.cardNumber)
                      : "•••• •••• •••• ••••";

                    return (
                      <div
                        key={card.id}
                        className="flex flex-col gap-4 rounded-2xl border border-zinc-100 bg-zinc-50 p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-zinc-900 shadow-sm">
                            <CreditCard className="h-5 w-5" />
                          </div>

                          <div>
                            <div className="text-sm font-semibold text-zinc-950">
                              {card.alias || brand}
                            </div>

                            <div className="mt-1 text-sm tracking-[0.16em] text-zinc-700">
                              {displayNumber}
                            </div>

                            <div className="mt-1 text-xs text-zinc-500">
                              {card.cardHolderName || "Kart sahibi bilgisi yok"}
                              {(card.expireMonth || card.expireYear) && (
                                <>
                                  {" "}
                                  • Son Kullanma: {card.expireMonth || "MM"}/
                                  {card.expireYear || "YY"}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-medium text-zinc-600">
                          {brand}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <section className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <h2 className="text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                Geçmiş Abonelikler
              </h2>

              <div className="mt-4 space-y-3">
                {sortedSubscriptions.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">
                    Geçmiş abonelik kaydı bulunamadı.
                  </div>
                ) : (
                  sortedSubscriptions.slice(0, 6).map((subscription) => {
                    const plan =
                      plans.find(
                        (item) =>
                          String(getPlanValue(item, "id")) ===
                          String(subscription.planId)
                      ) || null;

                    const planName =
                      getPlanValue(plan, "name") ||
                      getPlanValue(plan, "title") ||
                      `Plan #${subscription.planId}`;

                    return (
                      <div
                        key={subscription.id}
                        className="flex flex-col gap-3 rounded-2xl border border-zinc-100 bg-zinc-50 p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div>
                          <div className="font-medium text-zinc-950">
                            {planName}
                          </div>
                          <div className="mt-1 text-xs text-zinc-500">
                            {formatDate(subscription.startDate)} -{" "}
                            {formatDate(subscription.endDate)}
                          </div>
                        </div>

                        <div className="inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-medium text-zinc-700">
                          {getSubscriptionStatusLabel(subscription.status)}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-full bg-[#F8F8F8] px-5 pb-10 pt-4 md:hidden">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-extrabold tracking-[0.07em] text-[#EA580C]">
              ABONELİK & ÖDEME
            </p>
            <h1 className="mt-1 text-[24px] font-extrabold leading-7 text-[#18181B]">
              {mobileStep === 1
                ? "Planınızı seçin"
                : mobileStep === 2
                ? "Fatura bilgileri"
                : mobileStep === 3
                ? "Ödeme yöntemi"
                : "Ödemeyi kontrol edin"}
            </h1>
            <p className="mt-1 max-w-[310px] text-[12px] leading-[17px] text-[#71717A]">
              {mobileStep === 1
                ? "İşletmeniz için uygun abonelik planını seçin."
                : mobileStep === 2
                ? "Ödeme ve faturalama için gerekli bilgileri tamamlayın."
                : mobileStep === 3
                ? "Yeni veya kayıtlı kartınızla devam edin."
                : "Bilgileri kontrol edin ve 3D Secure ödeme adımına geçin."}
            </p>
          </div>

          <div className="flex min-h-8 shrink-0 items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-2.5 text-[10px] font-bold text-[#52525B]">
            <ShieldCheck className="h-3.5 w-3.5 text-[#EA580C]" />
            Güvenli
          </div>
        </div>

        <div className="mb-4 inline-flex rounded-full bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700">1 AY ÜCRETSİZ</div>

        <div className="mb-5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-[#18181B]">
              {mobileStep === 1
                ? "Plan"
                : mobileStep === 2
                ? "Bilgiler"
                : mobileStep === 3
                ? "Ödeme"
                : "Onay"}
            </span>
            <span className="text-[11px] font-bold text-[#A1A1AA]">
              {mobileStep} / 4
            </span>
          </div>

          <div className="mt-2 grid grid-cols-4 gap-1.5">
            {[1, 2, 3, 4].map((step) => (
              <span
                key={step}
                className={`h-1 rounded-full ${
                  mobileStep >= step ? "bg-[#EA580C]" : "bg-[#E4E4E7]"
                }`}
              />
            ))}
          </div>
        </div>

        {mobileStep === 1 ? (
          <div className="space-y-3">
            {plans.length === 0 ? (
              <div className="rounded-[18px] border border-dashed border-[#E4E4E7] bg-white p-5 text-center text-sm text-[#71717A]">
                Henüz tanımlı bir abonelik planı bulunmuyor.
              </div>
            ) : (
              plans.map((plan) => {
                const planId = getPlanValue(plan, "id");
                const selected = String(selectedPlanId) === String(planId);
                const planName =
                  getPlanValue(plan, "name") ||
                  getPlanValue(plan, "title") ||
                  "Plan";
                const planDescription =
                  getPlanValue(plan, "description") || "Abonelik planı";
                const planPrice = Number(getPlanValue(plan, "price") || 0);
                const currency = getPlanValue(plan, "currency") || "₺";

                return (
                  <button
                    key={planId}
                    type="button"
                    onClick={() => setSelectedPlanId(planId)}
                    className={`w-full rounded-[18px] border p-4 text-left transition active:scale-[0.99] ${
                      selected
                        ? "border-[#EA580C] bg-[#FFF7ED]"
                        : "border-[#E4E4E7] bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-[15px] font-extrabold ${
                            selected ? "text-[#9A3412]" : "text-[#18181B]"
                          }`}
                        >
                          {planName}
                        </p>
                        <p className="mt-1 text-[11px] leading-4 text-[#71717A]">
                          {planDescription}
                        </p>
                      </div>

                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                          selected
                            ? "border-[#EA580C] bg-[#EA580C] text-white"
                            : "border-[#D4D4D8] bg-white"
                        }`}
                      >
                        {selected ? "✓" : ""}
                      </span>
                    </div>

                    <PlanLimits plan={plan} />

                    <div className="mt-4 flex items-end justify-between gap-3 border-t border-[#E4E4E7]/70 pt-3">
                      <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-[#A1A1AA]">
                        KDV hariç
                      </span>
                      <span className="text-xl font-extrabold text-[#18181B]">
                        {formatMoney(planPrice, currency)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        ) : null}

        {mobileStep === 2 ? (
          <section className="rounded-[20px] border border-[#E4E4E7] bg-white p-4">
            <div className="space-y-4">
              <MobileField
                label="Adres başlığı"
                placeholder="Örn: İşletme, Ofis"
                value={address.title}
                onChange={(e) =>
                  setAddress((prev) => ({ ...prev, title: e.target.value }))
                }
                autoComplete="organization"
              />

              <MobileField
                label="İsim Soyisim"
                placeholder="Ad Soyad"
                value={address.contactName}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    contactName: e.target.value,
                  }))
                }
                autoComplete="name"
              />

              <div className="space-y-1.5">
                <label className="block text-[13px] font-bold text-[#3F3F46]">
                  Adres satırı
                </label>
                <textarea
                  rows={3}
                  value={address.addressLine}
                  onChange={(e) =>
                    setAddress((prev) => ({
                      ...prev,
                      addressLine: e.target.value,
                    }))
                  }
                  autoComplete="street-address"
                  placeholder="Mahalle, sokak, bina no, daire"
                  className="min-h-[94px] w-full resize-none rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 py-3 text-base text-[#18181B] outline-none focus:border-[#EA580C]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <MobileField
                  label="Şehir"
                  placeholder="İl"
                  value={address.city}
                  onChange={(e) =>
                    setAddress((prev) => ({ ...prev, city: e.target.value }))
                  }
                  autoComplete="address-level2"
                />

                <MobileField
                  label="Posta Kodu"
                  placeholder="19000"
                  value={address.zipCode}
                  onChange={(e) =>
                    setAddress((prev) => ({
                      ...prev,
                      zipCode: onlyDigits(e.target.value),
                    }))
                  }
                  inputMode="numeric"
                  autoComplete="postal-code"
                />
              </div>

              <MobileField
                label="TC Kimlik Numarası"
                placeholder="11 haneli TCKN"
                value={identityNumber}
                onChange={(e) =>
                  setIdentityNumber(onlyDigits(e.target.value).slice(0, 11))
                }
                inputMode="numeric"
                autoComplete="off"
                maxLength={11}
              />

              <div className="rounded-[15px] bg-[#FFF7ED] p-3.5">
                <p className="text-xs font-bold text-[#EA580C]">
                  Faturalama bilgisi
                </p>
                <p className="mt-1 text-[11px] leading-4 text-[#9A3412]">
                  Teslimat adresiniz ödeme işleminde fatura adresi olarak da
                  kullanılacaktır.
                </p>
              </div>
            </div>
          </section>
        ) : null}

        {mobileStep === 3 ? (
          <section className="rounded-[20px] border border-[#E4E4E7] bg-white p-4">
            <div className="grid grid-cols-2 gap-1 rounded-[14px] bg-[#F4F4F5] p-1">
              <button
                type="button"
                onClick={() => setPaymentMode("new")}
                className={`min-h-11 rounded-[11px] text-sm font-bold ${
                  paymentMode === "new"
                    ? "bg-white text-[#18181B] shadow-sm"
                    : "text-[#71717A]"
                }`}
              >
                Yeni Kart
              </button>

              <button
                type="button"
                disabled={registeredCards.length === 0}
                onClick={() => {
                  if (registeredCards.length) setPaymentMode("saved");
                }}
                className={`min-h-11 rounded-[11px] text-sm font-bold ${
                  paymentMode === "saved"
                    ? "bg-white text-[#18181B] shadow-sm"
                    : "text-[#71717A]"
                } ${registeredCards.length === 0 ? "opacity-40" : ""}`}
              >
                Kayıtlı Kart
              </button>
            </div>

            {paymentMode === "saved" ? (
              <div className="mt-4 space-y-2.5">
                {registeredCards.map((card) => {
                  const selected = selectedSavedCardId === card.id;

                  return (
                    <div
                      key={card.id ?? card.cardToken}
                      className={`rounded-[16px] border p-4 ${
                        selected
                          ? "border-[#EA580C] bg-[#FFF7ED]"
                          : "border-[#E4E4E7] bg-[#FAFAFA]"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => setSelectedSavedCardId(card.id)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <p className="text-sm font-extrabold text-[#18181B]">
                            {card.alias || getCardBrand(card.cardNumber)}
                          </p>
                          <p className="mt-2 text-sm tracking-[0.16em] text-[#52525B]">
                            {maskCardNumber(card.cardNumber)}
                          </p>
                          <p className="mt-1 text-[10px] text-[#A1A1AA]">
                            {card.cardHolderName || "Kart sahibi"}
                          </p>
                        </button>

                        <button
                          type="button"
                          aria-label="Kayıtlı kartı sil"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Kayıtlı kartı silmek istiyor musunuz?"
                              )
                            ) {
                              handleDeleteSavedCard(card.id);
                            }
                          }}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#DC2626] active:bg-[#FEF2F2]"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <MobileField
                  label="Kart üzerindeki isim"
                  placeholder="Ad Soyad"
                  value={cardHolderName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCardHolderName(value);
                    setAddress((prev) => ({
                      ...prev,
                      contactName: prev.contactName || value,
                    }));
                  }}
                  autoComplete="cc-name"
                />

                <MobileField
                  label="Kart numarası"
                  placeholder="1234 5678 9012 3456"
                  value={formatCardNumber(cardNumber)}
                  onChange={(e) =>
                    setCardNumber(onlyDigits(e.target.value).slice(0, 16))
                  }
                  inputMode="numeric"
                  autoComplete="cc-number"
                  maxLength={19}
                />

                <div className="grid grid-cols-3 gap-2.5">
                  <MobileField
                    label="Ay"
                    placeholder="MM"
                    value={expireMonth}
                    onChange={(e) =>
                      setExpireMonth(onlyDigits(e.target.value).slice(0, 2))
                    }
                    inputMode="numeric"
                    autoComplete="cc-exp-month"
                    maxLength={2}
                  />
                  <MobileField
                    label="Yıl"
                    placeholder="YY"
                    value={expireYear}
                    onChange={(e) =>
                      setExpireYear(onlyDigits(e.target.value).slice(0, 4))
                    }
                    inputMode="numeric"
                    autoComplete="cc-exp-year"
                    maxLength={4}
                  />
                  <MobileField
                    label="CVC"
                    placeholder="123"
                    type="password"
                    value={cvc}
                    onChange={(e) =>
                      setCvc(onlyDigits(e.target.value).slice(0, 4))
                    }
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    maxLength={4}
                  />
                </div>

                <label className="flex min-h-12 items-center justify-between gap-3 rounded-[14px] bg-[#F4F4F5] px-3.5">
                  <span className="text-[13px] font-semibold text-[#18181B]">
                    Kartımı kaydet
                  </span>
                  <input
                    type="checkbox"
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                    className="h-5 w-5 accent-[#EA580C]"
                  />
                </label>
              </div>
            )}

            <label className="mt-3 flex min-h-12 items-center justify-between gap-3 rounded-[14px] bg-[#F4F4F5] px-3.5">
              <div>
                <p className="text-[13px] font-semibold text-[#18181B]">
                  Otomatik yenileme
                </p>
                <p className="mt-0.5 text-[10px] text-[#71717A]">
                  Dönem sonunda plan otomatik yenilensin
                </p>
              </div>
              <input
                type="checkbox"
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
                className="h-5 w-5 accent-[#EA580C]"
              />
            </label>
          </section>
        ) : null}

        {mobileStep === 4 ? (
          <section className="rounded-[20px] border border-[#E4E4E7] bg-white p-4">
            <div className="rounded-[17px] bg-[#18181B] p-4 text-white">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#A1A1AA]">
                    Seçili plan
                  </p>
                  <p className="mt-1 text-lg font-extrabold">
                    {selectedPlanName}
                  </p>
                </div>
                <CheckCircle2 className="h-5 w-5 text-green-400" />
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <MobileSummaryRow
                label="Ödeme yöntemi"
                value={paymentMode === "saved" ? "Kayıtlı Kart" : "Yeni Kart"}
              />
              <MobileSummaryRow label="Şehir" value={address.city || "-"} />
              <MobileSummaryRow
                label="Otomatik yenileme"
                value={autoRenew ? "Açık" : "Kapalı"}
              />

              <div className="border-t border-[#E4E4E7] pt-3">
                <MobileSummaryRow
                  label="Ara Toplam"
                  value={
                    selectedPlan
                      ? formatMoney(subtotal, selectedCurrency)
                      : "--"
                  }
                />
                <MobileSummaryRow
                  label={`KDV (%${vatPercent})`}
                  value={
                    selectedPlan
                      ? formatMoney(vatPrice, selectedCurrency)
                      : "--"
                  }
                />
              </div>

              <div className="flex items-end justify-between gap-3 border-t border-[#E4E4E7] pt-4">
                <span className="text-xs font-semibold text-[#71717A]">
                  Toplam
                </span>
                <span className="text-[28px] font-extrabold text-[#18181B]">
                  {selectedPlan
                    ? formatMoney(vatIncludedPrice, selectedCurrency)
                    : "--"}
                </span>
              </div>
            </div>

            <div className="mt-4 rounded-[15px] bg-[#FFF7ED] p-3.5">
              <div className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#EA580C]" />
                <p className="text-[11px] leading-4 text-[#9A3412]">
                  Devam ettiğinizde bankanızın 3D Secure doğrulama ekranı
                  açılacaktır.
                </p>
              </div>
            </div>
          </section>
        ) : null}

        <div className="-mx-5 mt-5 border-t border-[#E4E4E7] bg-white/95 px-5 py-3 backdrop-blur">
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleMobileBack}
              disabled={mobileStep === 1 || loading}
              className="min-h-[52px] rounded-[15px] border border-[#E4E4E7] bg-white text-[15px] font-bold text-[#18181B] disabled:opacity-40"
            >
              Geri
            </button>

            <button
              type="button"
              onClick={handleMobileContinue}
              disabled={
                loading ||
                (mobileStep === 1 && !selectedPlanId) ||
                (mobileStep === 3 &&
                  paymentMode === "saved" &&
                  registeredCards.length === 0)
              }
              className="min-h-[52px] rounded-[15px] bg-[#EA580C] px-3 text-[15px] font-bold text-white disabled:opacity-40"
            >
              {loading
                ? "İşleniyor..."
                : mobileStep === 4
                ? "3D Secure ile Öde"
                : "Devam"}
            </button>
          </div>
        </div>
      </div>

      <div className="hidden w-full justify-center md:flex">
        <div className="w-full max-w-[1180px] space-y-6">
          <div className="relative overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_22px_65px_rgba(24,24,27,0.05)] md:flex md:items-start md:justify-between md:gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.13em] text-orange-700">
                <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                Abonelik & Ödeme
              </div>
              <div className="mt-3 inline-flex rounded-full bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700">1 AY ÜCRETSİZ</div>
              <h1 className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[40px]">
                Web Menü aboneliğinizi yönetin.
              </h1>
              <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-zinc-500">
                İşletmeniz için uygun süreyi seçin, ödeme bilgilerinizi tamamlayın
                ve Web Menü vitrininizi kesintisiz kullanmaya devam edin.
              </p>
            </div>

            <div className="inline-flex shrink-0 items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-bold text-emerald-700">
              <ShieldCheck className="h-4 w-4" />
              Güvenli ödeme altyapısı
            </div>
          </div>

          <div className="grid items-start gap-6 xl:grid-cols-[0.92fr_1.38fr_0.92fr]">
            <section className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-orange-50 text-[#EA580C]">
                  <Wallet className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                    Abonelik Planları
                  </h2>
                  <p className="mt-1 text-xs font-medium text-zinc-500">
                    İşletmen için en uygun paketi seç.
                  </p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {plans.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 p-5 text-sm text-zinc-500">
                    Henüz tanımlı bir abonelik planı bulunmuyor.
                  </div>
                )}

                {plans.map((plan) => {
                  const planId = getPlanValue(plan, "id");
                  const isActive = String(selectedPlanId) === String(planId);

                  const planName =
                    getPlanValue(plan, "name") ||
                    getPlanValue(plan, "title") ||
                    "Plan";

                  const planDescription =
                    getPlanValue(plan, "description") ||
                    "Abonelik planı açıklaması.";

                  const rawPrice = Number(getPlanValue(plan, "price") || 0);
                  const currency = getPlanValue(plan, "currency") || "₺";

                  return (
                    <button
                      key={planId}
                      type="button"
                      onClick={() => setSelectedPlanId(planId)}
                      className={`relative w-full overflow-hidden rounded-[22px] border p-4 text-left transition-all ${
                        isActive
                          ? "border-orange-300 bg-orange-50 text-zinc-950 shadow-[0_12px_30px_rgba(234,88,12,0.10)]"
                          : "border-zinc-200 bg-white hover:border-orange-200 hover:bg-[#FFFCFA]"
                      }`}
                    >
                      <div className="pr-20">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">
                            {planName}
                          </span>

                          {isActive && (
                            <span className="rounded-full bg-[#EA580C] px-2 py-0.5 text-[11px] font-bold text-white">
                              Seçili
                            </span>
                          )}
                        </div>

                        <p
                          className={`mt-1 text-xs ${
                            isActive ? "text-zinc-200" : "text-zinc-500"
                          }`}
                        >
                          {planDescription}
                        </p>
                      </div>

                      <PlanLimits plan={plan} />

                      <div className="mt-4 flex items-end justify-between gap-4">
                        <div>
                          <div className="text-2xl font-bold leading-none">
                            {formatMoney(rawPrice, currency)}
                          </div>

                          <div
                            className={`mt-1 text-xs ${
                              isActive ? "text-zinc-300" : "text-zinc-500"
                            }`}
                          >
                            KDV hariç plan fiyatı
                          </div>
                        </div>

                        <div className="text-right">
                          <div
                            className={`text-[11px] uppercase tracking-[0.14em] ${
                              isActive ? "text-zinc-300" : "text-zinc-400"
                            }`}
                          >
                            Kullanım süresi
                          </div>
                          <div className="mt-1 text-sm font-semibold">
                            {getPlanValue(plan, "durationDays") != null
                              ? `${getPlanValue(plan, "durationDays")} gün`
                              : "-"}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="space-y-6">
              <div className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-orange-50 text-[#EA580C]">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                      Teslimat Adresi
                    </h2>
                    <p className="mt-1 text-xs font-medium text-zinc-500">
                      Ödeme ve faturalama sürecinde kullanılacak bilgiler.
                    </p>
                  </div>
                </div>

                <div className="mt-5 space-y-3">
                  <InputField
                    label="Adres Başlığı"
                    placeholder="Örn: Ev, Ofis"
                    value={address.title}
                    onChange={(e) =>
                      setAddress((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />

                  <InputField
                    label="İsim Soyisim"
                    placeholder="Ad Soyad"
                    value={address.contactName}
                    onChange={(e) =>
                      setAddress((prev) => ({
                        ...prev,
                        contactName: e.target.value,
                      }))
                    }
                  />

                  <TextareaField
                    label="Adres Satırı"
                    placeholder="Mahalle, sokak, bina no, daire"
                    value={address.addressLine}
                    onChange={(e) =>
                      setAddress((prev) => ({
                        ...prev,
                        addressLine: e.target.value,
                      }))
                    }
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <InputField
                      label="Şehir"
                      placeholder="İl"
                      value={address.city}
                      onChange={(e) =>
                        setAddress((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                    />

                    <InputField
                      label="Posta Kodu"
                      placeholder="Posta Kodu"
                      value={address.zipCode}
                      onChange={(e) =>
                        setAddress((prev) => ({
                          ...prev,
                          zipCode: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <InputField
                    label="TC Kimlik Numarası"
                    placeholder="11 haneli TCKN"
                    value={identityNumber}
                    onChange={(e) =>
                      setIdentityNumber(onlyDigits(e.target.value))
                    }
                    maxLength={11}
                  />
                </div>
              </div>

              <div className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-[14px] bg-orange-50 text-[#EA580C]">
                    <CreditCard className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                      Ödeme Yöntemi
                    </h2>
                    <p className="mt-1 text-xs font-medium text-zinc-500">
                      Yeni kart veya kayıtlı kart ile devam et.
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2 rounded-2xl bg-zinc-100 p-1.5">
                  <button
                    type="button"
                    onClick={() => setPaymentMode("new")}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                      paymentMode === "new"
                        ? "bg-white text-zinc-900 shadow-sm"
                        : "text-zinc-600"
                    }`}
                  >
                    Yeni Kart
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (registeredCards.length > 0) setPaymentMode("saved");
                    }}
                    disabled={registeredCards.length === 0}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                      paymentMode === "saved"
                        ? "bg-white text-zinc-900 shadow-sm"
                        : "text-zinc-600"
                    } ${
                      registeredCards.length === 0
                        ? "cursor-not-allowed opacity-50"
                        : ""
                    }`}
                  >
                    Kayıtlı Kart
                  </button>
                </div>

                <div className="mt-5">
                  {paymentMode === "saved" ? (
                    <div className="space-y-3">
                      {loadingCards ? (
                        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500">
                          Kayıtlı kartlar yükleniyor...
                        </div>
                      ) : registeredCards.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500">
                          Kayıtlı kart bulunamadı. Yeni kart sekmesinden ödeme
                          yapabilirsin.
                        </div>
                      ) : (
                        registeredCards.map((card) => {
                          const isSelected = selectedSavedCardId === card.id;
                          const brand = getCardBrand(card.cardNumber);
                          const displayNumber = card.cardNumber
                            ? maskCardNumber(card.cardNumber)
                            : "•••• •••• •••• ••••";

                          return (
                            <div
                              key={card.id}
                              className={`rounded-2xl border p-4 transition ${
                                isSelected
                                  ? "border-zinc-900 bg-zinc-50"
                                  : "border-zinc-200 bg-white"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedSavedCardId(card.id)
                                  }
                                  className="flex-1 text-left"
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm font-semibold text-zinc-900">
                                      {card.alias || brand}
                                    </span>
                                    <span className="mt-1 text-xs font-medium text-zinc-500">
                                      {brand}
                                    </span>
                                  </div>

                                  <div className="mt-2 text-sm tracking-[0.18em] text-zinc-800">
                                    {displayNumber}
                                  </div>

                                  <div className="mt-2 text-xs text-zinc-500">
                                    {card.cardHolderName || "Kart sahibi"}
                                    {(card.expireMonth || card.expireYear) && (
                                      <>
                                        {" "}
                                        • {card.expireMonth}/{card.expireYear}
                                      </>
                                    )}
                                  </div>
                                </button>

                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDeleteSavedCard(card.id)
                                  }
                                  className="rounded-xl p-2 transition hover:bg-zinc-100"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <InputField
                        label="Kart Üzerindeki İsim"
                        placeholder="Ad Soyad"
                        value={cardHolderName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCardHolderName(value);
                          setAddress((prev) => ({
                            ...prev,
                            contactName: prev.contactName || value,
                          }));
                        }}
                      />

                      <InputField
                        label="Kart Numarası"
                        placeholder="1234 5678 9012 3456"
                        value={formatCardNumber(cardNumber)}
                        onChange={(e) =>
                          setCardNumber(onlyDigits(e.target.value))
                        }
                        maxLength={19}
                      />

                      <div className="grid grid-cols-3 gap-3">
                        <InputField
                          label="Ay (MM)"
                          placeholder="07"
                          value={expireMonth}
                          onChange={(e) =>
                            setExpireMonth(onlyDigits(e.target.value))
                          }
                          maxLength={2}
                        />

                        <InputField
                          label="Yıl (YY / YYYY)"
                          placeholder="28"
                          value={expireYear}
                          onChange={(e) =>
                            setExpireYear(onlyDigits(e.target.value))
                          }
                          maxLength={4}
                        />

                        <InputField
                          label="CVC"
                          placeholder="123"
                          value={cvc}
                          onChange={(e) => setCvc(onlyDigits(e.target.value))}
                          maxLength={4}
                        />
                      </div>

                      <label className="inline-flex items-center gap-2 text-xs text-zinc-700">
                        <input
                          type="checkbox"
                          className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900/40"
                          checked={saveCard}
                          onChange={(e) => setSaveCard(e.target.checked)}
                        />
                        Kartımı sonraki ödemeler için kaydet
                      </label>
                    </div>
                  )}

                  <label className="mt-4 inline-flex items-center gap-2 text-xs text-zinc-700">
                    <input
                      type="checkbox"
                      className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900/40"
                      checked={autoRenew}
                      onChange={(e) => setAutoRenew(e.target.checked)}
                    />
                    Otomatik yenileme açık olsun
                  </label>
                </div>
              </div>
            </section>

            <aside className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.06)] sm:sticky sm:top-6">
              <h3 className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                Sipariş Özeti
              </h3>

              <div className="mt-5 rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-4 text-white shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-300">
                    Kart Önizleme
                  </span>
                  <span className="text-xs text-zinc-300">{previewBrand}</span>
                </div>

                <div className="mt-5 text-sm tracking-[0.22em] text-white/95">
                  {previewNumber}
                </div>

                <div className="mt-6 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                      Kart Sahibi
                    </div>
                    <div className="mt-1 text-sm font-medium uppercase text-white">
                      {previewName}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                      Son Kullanma
                    </div>
                    <div className="mt-1 text-sm font-medium text-white">
                      {`${previewMonth}/${previewYear}`}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Plan</span>
                  <span className="text-right font-medium text-zinc-900">
                    {selectedPlanName}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Ödeme Türü</span>
                  <span className="font-medium text-zinc-900">
                    {paymentMode === "saved" ? "Kayıtlı Kart" : "Yeni Kart"}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Otomatik Yenileme</span>
                  <span className="font-medium text-zinc-900">
                    {autoRenew ? "Açık" : "Kapalı"}
                  </span>
                </div>

                {selectedPlan && (
                  <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                    <div className="mt-1 text-xs font-medium text-zinc-500">Seçili plan</div>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-zinc-900">
                          {selectedPlanName}
                        </div>
                        <div className="mt-1 text-xs font-medium text-zinc-500">
                          {selectedPlanDescription}
                        </div>
                      </div>
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-2 border-t border-zinc-100 pt-5">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Ara Toplam</span>
                  <span className="font-medium text-zinc-900">
                    {selectedPlan
                      ? formatMoney(subtotal, selectedCurrency)
                      : `-- ${selectedCurrency}`}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">
                    KDV {vatPercent > 0 ? `(%${vatPercent})` : ""}
                  </span>
                  <span className="font-medium text-zinc-900">
                    {selectedPlan
                      ? formatMoney(vatPrice, selectedCurrency)
                      : `-- ${selectedCurrency}`}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t pt-3">
                  <span className="mt-1 text-xs font-medium text-zinc-500">Toplam tutar</span>
                  <span className="text-3xl font-bold text-zinc-900">
                    {selectedPlan
                      ? formatMoney(vatIncludedPrice, selectedCurrency)
                      : "--"}
                  </span>
                </div>

                <p className="mt-2 text-xs leading-5 text-zinc-500">
                  Ödeme tamamlandıktan sonra aboneliğiniz aktifleşir ve Web Menü'nüzü
                  kullanmaya devam edebilirsiniz.
                </p>

                <Button
                  onClick={handlePay}
                  disabled={
                    loading ||
                    !selectedPlanId ||
                    (paymentMode === "saved" && registeredCards.length === 0)
                  }
                  className="mt-5 h-11 w-full rounded-full bg-[#EA580C] font-bold text-white shadow-[0_12px_26px_rgba(234,88,12,0.20)] hover:bg-[#C2410C]"
                >
                  {loading
                    ? "Ödeme İşleniyor..."
                    : selectedPlanId
                    ? "Ödemeyi Tamamla"
                    : "Plan Seçin"}
                </Button>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <Iyzico3DSModal
        open={payModalOpen}
        onClose={closePayModal}
        htmlContent={payHtmlContent}
      />
    </AdminLayout>
  );
}

function MobileField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  inputMode,
  autoComplete,
  maxLength,
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[13px] font-bold text-[#3F3F46]">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        maxLength={maxLength}
        className="h-[52px] w-full rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 text-base text-[#18181B] outline-none focus:border-[#EA580C]"
      />
    </div>
  );
}

function MobileSummaryRow({ label, value }) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-3 text-[13px]">
      <span className="text-[#71717A]">{label}</span>
      <span className="text-right font-bold text-[#18181B]">{value}</span>
    </div>
  );
}

function InputField({ label, value, onChange, placeholder, maxLength }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-zinc-700">{label}</label>
      <input
        className="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm outline-none transition focus:border-zinc-400 focus:bg-white focus:ring-4 focus:ring-zinc-900/5"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
      />
    </div>
  );
}

function TextareaField({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-zinc-700">{label}</label>
      <textarea
        className="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm outline-none transition focus:border-zinc-400 focus:bg-white focus:ring-4 focus:ring-zinc-900/5"
        rows={3}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}
